import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { Document, Page, Text, View, Image, StyleSheet, PDFDownloadLink } from "@react-pdf/renderer";
import { DownloadSimple, MapPin, CalendarBlank, CheckCircle, ArrowsClockwise, QrCode } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/api";

const pdf = StyleSheet.create({
  page: { backgroundColor: "#050505", padding: 30 },
  card: { border: "1pt solid #222", borderRadius: 8, overflow: "hidden" },
  header: { backgroundColor: "#00F0FF", padding: 16 },
  brand: { color: "#000", fontSize: 20, fontFamily: "Helvetica-Bold" },
  body: { backgroundColor: "#111", padding: 20, flexDirection: "row", justifyContent: "space-between" },
  eventName: { color: "#fff", fontSize: 18, fontFamily: "Helvetica-Bold", marginBottom: 8 },
  meta: { color: "#aaa", fontSize: 10, marginBottom: 4 },
  label: { color: "#00F0FF", fontSize: 8, marginTop: 10, fontFamily: "Helvetica-Bold" },
  value: { color: "#fff", fontSize: 12, marginBottom: 2 },
  qr: { width: 130, height: 130, backgroundColor: "#fff", padding: 6 },
  token: { color: "#555", fontSize: 7, marginTop: 20 },
});

function TicketPDF({ ticket, qr }) {
  return (
    <Document>
      <Page size="A5" style={pdf.page}>
        <View style={pdf.card}>
          <View style={pdf.header}><Text style={pdf.brand}>EVENTKU · E-TICKET</Text></View>
          <View style={pdf.body}>
            <View style={{ flex: 1, paddingRight: 12 }}>
              <Text style={pdf.eventName}>{ticket.event_name}</Text>
              <Text style={pdf.meta}>{ticket.event_date}</Text>
              <Text style={pdf.meta}>{ticket.event_location}</Text>
              <Text style={pdf.label}>PENGUNJUNG</Text>
              <Text style={pdf.value}>{ticket.attendee_name}</Text>
              <Text style={pdf.label}>JENIS TIKET</Text>
              <Text style={pdf.value}>{ticket.ticket_type_name}</Text>
              <Text style={pdf.token}>{ticket.token}</Text>
            </View>
            {qr && <Image style={pdf.qr} src={qr} />}
          </View>
        </View>
      </Page>
    </Document>
  );
}

export function WalletTicket({ ticket, index = 0 }) {
  const [qr, setQr] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const attended = ticket.status === "attended";

  useEffect(() => {
    QRCode.toDataURL(ticket.token, { width: 320, margin: 1, color: { dark: "#000000", light: "#ffffff" } }).then(setQr);
  }, [ticket.token]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="[perspective:1600px]"
      data-testid={`wallet-ticket-${ticket.id}`}
    >
      <motion.div
        className="relative [transform-style:preserve-3d] w-full"
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ minHeight: 236 }}
      >
        {/* FRONT — boarding pass */}
        <div className="absolute inset-0 [backface-visibility:hidden]">
          <div className={`flex h-full bg-card border ${attended ? "border-white/10" : "border-primary/40"} overflow-hidden`}>
            {/* accent strip */}
            <div className={`w-2 ${attended ? "bg-zinc-700" : "bg-primary"}`} />
            <div className="flex-1 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] bg-primary text-black px-2 py-0.5">{ticket.ticket_type_name}</span>
                {attended ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-1"><CheckCircle weight="fill" size={13} /> Sudah masuk</span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#00FF66]">● Aktif</span>
                )}
              </div>
              <h3 className="font-heading font-bold text-2xl text-white leading-tight mb-3">{ticket.event_name}</h3>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-auto">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-zinc-600">Tanggal</p>
                  <p className="text-sm text-zinc-300 flex items-center gap-1"><CalendarBlank size={13} className="text-primary" /> {formatDateTime(ticket.event_date)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-zinc-600">Pengunjung</p>
                  <p className="text-sm text-zinc-300 truncate">{ticket.attendee_name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[9px] uppercase tracking-widest text-zinc-600">Lokasi</p>
                  <p className="text-sm text-zinc-300 flex items-center gap-1"><MapPin size={13} className="text-primary" /> {ticket.event_location}</p>
                </div>
              </div>
              <button data-testid={`flip-ticket-${ticket.id}`} onClick={() => setFlipped(true)}
                className="mt-4 self-start inline-flex items-center gap-2 bg-primary text-black text-sm font-bold px-4 py-2 hover:bg-cyan-300 transition-colors">
                <QrCode size={16} weight="bold" /> Tampilkan QR
              </button>
            </div>
            {/* perforated stub */}
            <div className="w-24 border-l border-dashed border-white/20 bg-black/40 flex flex-col items-center justify-center gap-2 shrink-0">
              {qr && <img src={qr} alt="qr" className={`w-14 h-14 bg-white p-0.5 ${attended ? "opacity-40 grayscale" : ""}`} />}
              <span className="text-[8px] uppercase tracking-widest text-zinc-600 rotate-90 whitespace-nowrap mt-2">EVENTKU</span>
            </div>
          </div>
        </div>

        {/* BACK — big QR */}
        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]">
          <div className="flex h-full bg-card border border-primary/40 overflow-hidden">
            <div className="flex-1 flex flex-col items-center justify-center p-6 gap-3">
              {qr ? (
                <img data-testid={`ticket-qr-${ticket.id}`} src={qr} alt="QR" className={`w-40 h-40 bg-white p-2 ${attended ? "opacity-40 grayscale" : "neon-glow"}`} />
              ) : (
                <div className="w-40 h-40 bg-white/5 animate-pulse" />
              )}
              <p className="text-[9px] text-zinc-600 font-mono">{ticket.token.slice(0, 20)}</p>
              <p className="text-xs text-zinc-500 text-center max-w-[220px]">Tunjukkan QR ini ke petugas di pintu masuk untuk validasi.</p>
            </div>
            <div className="w-40 border-l border-dashed border-white/15 p-5 flex flex-col justify-center gap-3 bg-black/30">
              <p className="font-heading font-bold text-white leading-tight">{ticket.event_name}</p>
              <p className="text-xs text-zinc-400">{ticket.ticket_type_name}</p>
              {qr && (
                <PDFDownloadLink document={<TicketPDF ticket={ticket} qr={qr} />} fileName={`eticket-${ticket.token.slice(0, 8)}.pdf`}>
                  {({ loading }) => (
                    <button data-testid={`download-pdf-${ticket.id}`}
                      className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-primary hover:text-black text-white text-xs font-semibold px-3 py-2 border border-white/15 transition-colors">
                      <DownloadSimple size={14} weight="bold" /> {loading ? "..." : "PDF"}
                    </button>
                  )}
                </PDFDownloadLink>
              )}
              <button data-testid={`unflip-ticket-${ticket.id}`} onClick={() => setFlipped(false)}
                className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-primary text-xs font-semibold">
                <ArrowsClockwise size={14} weight="bold" /> Balik kartu
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
